import { StrictMode, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { Tooltip, darkThemeClass, globalCss } from '@streamelements/frontend-ui';
import { Toasts } from './components/Toast';
import { createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { getServiceEnvironment, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { createAuthContextProvider } from './utils/auth';
import { QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ensureOBSPresence } from './utils/ensureOBSPresence';
import { promisefy } from './utils/promisefy';
import { shouldReportErrorToSentry } from './types/errors';

Sentry.init({
  dsn: 'https://b65a0c14d83d818eaad291dee2a61d77@o127985.ingest.us.sentry.io/4508716533219328',
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
    }),
    Sentry.browserTracingIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transaction
  replaysSessionSampleRate: 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend(event, hint) {
    if (!shouldReportErrorToSentry(hint?.originalException)) {
      return null;
    }
    
    return event;
  },
});

Sentry.setTag('buildDateTime', GLOBAL_BUILD_DATETIME);
Sentry.setTag('buildTimestamp', GLOBAL_BUILD_TIMESTAMP);
Sentry.setTag('buildGitSha', GLOBAL_BUILD_GIT_SHA);

console.info('Build stamp: ', { GLOBAL_BUILD_DATETIME: new Date(GLOBAL_BUILD_TIMESTAMP).toISOString(), GLOBAL_BUILD_TIMESTAMP, GLOBAL_BUILD_GIT_SHA });

const serviceEnvironment: ServiceEnvironment = {
  ...getServiceEnvironment('production'),
  ...{
    get isServerEnvironment() {
      return true;
    },
    get isBrowserEnvironment() {
      return false;
    },
  },
};

export const authContextProvider = createAuthContextProvider(serviceEnvironment);

export const queryClient = new QueryClient();

export const router = createRouter({
  routeTree,
  context: {
    queryClient,
    serviceEnvironment,
    authContextProvider,
    sharedAstroConnection: undefined!,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function onThemeChange() {
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  document.body?.classList?.add(darkThemeClass);

  if (isDarkMode) {
    // document.body.classList.add(darkThemeClass);
    return;
  }

  // document.body.classList.remove(darkThemeClass);
}

setTimeout(onThemeChange, 0);

window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', onThemeChange);

globalCss({
  body: {
    bgc: '$uiTertiary',
    color: '$textPrimaryMain',
    fontFamily: '$base',
    fontSize: '16px',
  },
})();

async function render() {
  // Render the app
  const rootElement = document.getElementById('root')!;

  function HostProperties() {
    useEffect(() => {
      (async () => {
        await ensureOBSPresence(false);
        const { hostPluginVersionString } = await promisefy(window.host.getHostProperties)();

        Sentry.setTag('hostPluginVersionString', hostPluginVersionString);
      })();
    }, []);

    return null;
  }

  if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <HostProperties />
        <QueryClientProvider client={queryClient}>
          <Toasts>
            <Tooltip.Provider delayDuration={0}>
              <RouterProvider router={router} />
              {/* <ReactQueryDevtools /> */}
            </Tooltip.Provider>
          </Toasts>
          {/* <ReactQueryDevtools /> */}
        </QueryClientProvider>
      </StrictMode>
    );
  }

  Object.assign(window, {
    clearCanvases() {
      window.host.getAllDockingWidgets((widgets) => {
        for (const widget in widgets) {
          if (widget.startsWith('streamelements_selive_multistreaming_canvas_')) {
            window.host.removeDockingWidgetsByIds([widget], () => {});
          }
        }
      });

      window.host.removeVideoCompositionViewOverlay(() => {
        window.host.getAllVideoCompositions((compositions) => {
          for (const comp in compositions) {
            if (comp === 'default') {
              continue;
            }

            window.host.removeVideoCompositionsByIds([comp], () => {});
          }
        });
      });
    },
  });
}

if (location.pathname.startsWith('/auth/')) {
  // Assume host is ready when we're in auth flow (browser)
  render();
} else {
  if (window.host?.hostReady) {
    render();
  } else {
    window.addEventListener('hostReady', render, { once: true });
  }
}
